export function locationHasPtpOrDark(location) {
  const types = Array.isArray(location?.connectionTypes)
    ? location.connectionTypes
    : Array.isArray(location?.connection_types)
      ? location.connection_types
      : [];
  return types.some((t) => t === "PTP" || t === "Dark");
}

export function locationHasDia(location) {
  const types = Array.isArray(location?.connectionTypes)
    ? location.connectionTypes
    : Array.isArray(location?.connection_types)
      ? location.connection_types
      : [];
  return types.includes("DIA");
}

export function emptyCrmLocation() {
  return {
    departments: [],
    serviceLocation: "",
    connectionTypes: [],
    aLocation: "",
    zLocation: "",
    ips: "",
    serviceSpeed: "",
    mrc: "",
    nrc: "",
    subtype: "",
    notes: "",
    siteContactName: "",
    siteContactEmail: "",
    siteContactPhone: "",
    itContactName: "",
    itContactEmail: "",
    itContactPhone: "",
  };
}

export function emptyOpportunityForm() {
  return {
    companyId: "",
    title: "",
    status: "open",
    projectType: "Revenue",
    newCustomer: false,
    termInMonths: "",
    salesRep: "",
    notes: "",
    siteContactPersonId: "",
    itContactPersonId: "",
    siteContactName: "",
    siteContactEmail: "",
    siteContactPhone: "",
    itContactName: "",
    itContactEmail: "",
    itContactPhone: "",
    projectIds: [],
    locations: [emptyCrmLocation()],
  };
}

export function opportunityToForm(row) {
  if (!row) return emptyOpportunityForm();
  const locations = Array.isArray(row.locations) && row.locations.length
    ? row.locations.map((loc) => ({
        ...emptyCrmLocation(),
        id: loc.id,
        departments: Array.isArray(loc.departments) ? loc.departments : [],
        serviceLocation: loc.serviceLocation || "",
        connectionTypes: Array.isArray(loc.connectionTypes) ? loc.connectionTypes : [],
        aLocation: loc.aLocation || "",
        zLocation: loc.zLocation || "",
        ips: loc.ips || "",
        serviceSpeed: loc.serviceSpeed || "",
        mrc: loc.mrc ?? "",
        nrc: loc.nrc ?? "",
        subtype: loc.subtype || "",
        notes: loc.notes || "",
        siteContactName: loc.siteContactName || "",
        siteContactEmail: loc.siteContactEmail || "",
        siteContactPhone: loc.siteContactPhone || "",
        itContactName: loc.itContactName || "",
        itContactEmail: loc.itContactEmail || "",
        itContactPhone: loc.itContactPhone || "",
      }))
    : [emptyCrmLocation()];
  const projectIds = Array.isArray(row.projects)
    ? row.projects.map((p) => p.id).filter((id) => id != null)
    : row.projectId
      ? [row.projectId]
      : [];
  return {
    companyId: row.companyId || "",
    title: row.title || "",
    status: row.status || "open",
    projectType: row.projectType || "Revenue",
    newCustomer: Boolean(row.newCustomer),
    termInMonths: row.termInMonths ?? "",
    salesRep: row.salesRep || "",
    notes: row.notes || "",
    siteContactPersonId: row.siteContactPersonId || "",
    itContactPersonId: row.itContactPersonId || "",
    siteContactName: row.siteContactName || "",
    siteContactEmail: row.siteContactEmail || "",
    siteContactPhone: row.siteContactPhone || "",
    itContactName: row.itContactName || "",
    itContactEmail: row.itContactEmail || "",
    itContactPhone: row.itContactPhone || "",
    projectIds,
    locations,
  };
}

export function formToOpportunityPayload(form, { includeProjectIds = true } = {}) {
  // Save never Close-Won's an opportunity — that only happens via the Close Won action.
  const status = form.status === "lost" ? "lost" : "open";
  const projectIds = Array.isArray(form.projectIds)
    ? form.projectIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
    : [];
  const payload = {
    companyId: form.companyId ? Number(form.companyId) : null,
    title: form.title,
    status,
    projectType: form.projectType,
    newCustomer: Boolean(form.newCustomer),
    termInMonths: form.termInMonths,
    salesRep: form.salesRep,
    notes: form.notes,
    siteContactPersonId: form.siteContactPersonId || null,
    itContactPersonId: form.itContactPersonId || null,
    siteContactName: form.siteContactName,
    siteContactEmail: form.siteContactEmail,
    siteContactPhone: form.siteContactPhone,
    itContactName: form.itContactName,
    itContactEmail: form.itContactEmail,
    itContactPhone: form.itContactPhone,
    locations: (form.locations || []).map((loc, index) => ({
      sortOrder: index,
      departments: loc.departments,
      serviceLocation: loc.serviceLocation,
      connectionTypes: loc.connectionTypes,
      aLocation: loc.aLocation,
      zLocation: loc.zLocation,
      ips: loc.ips,
      serviceSpeed: loc.serviceSpeed,
      mrc: loc.mrc,
      nrc: loc.nrc,
      subtype: loc.subtype,
      notes: loc.notes,
      siteContactName: loc.siteContactName || form.siteContactName,
      siteContactEmail: loc.siteContactEmail || form.siteContactEmail,
      siteContactPhone: loc.siteContactPhone || form.siteContactPhone,
      itContactName: loc.itContactName || form.itContactName,
      itContactEmail: loc.itContactEmail || form.itContactEmail,
      itContactPhone: loc.itContactPhone || form.itContactPhone,
    })),
  };
  if (includeProjectIds) payload.projectIds = projectIds;
  return payload;
}

/** Closed opportunities: notes + linked work orders only. */
export function formToClosedOpportunityPayload(form) {
  const projectIds = Array.isArray(form.projectIds)
    ? form.projectIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
    : [];
  return {
    notes: form.notes,
    projectIds,
    status: "closed",
  };
}

export function validateOpportunityForClose(form) {
  const locations = form?.locations || [];
  if (!form?.companyId) return "Select a company.";
  const revenue = String(form.projectType || "Revenue").toLowerCase() === "revenue";
  for (let i = 0; i < locations.length; i += 1) {
    const loc = locations[i];
    const label = locations.length > 1 ? `Location ${i + 1}` : "Location";
    if (!loc.departments?.length) return `${label}: select at least one department.`;
    if (!revenue) continue;
    if (!String(loc.serviceLocation || "").trim()) return `${label}: service location is required.`;
    if (!loc.connectionTypes?.length) return `${label}: select at least one service type.`;
    if (locationHasDia(loc)) {
      if (!String(loc.serviceSpeed || "").trim()) return `${label}: service speed is required for DIA.`;
      if (!String(loc.ips || "").trim()) return `${label}: IPs selection is required for DIA.`;
    }
    if (locationHasPtpOrDark(loc)) {
      if (!String(loc.aLocation || "").trim() || !String(loc.zLocation || "").trim()) {
        return `${label}: A and Z locations are required for PTP or Dark.`;
      }
    }
  }
  return "";
}
